import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGlobalVariable,
  useUpdateGlobalVariable
} from '@api/enterprise/globalVariable';
import { globalVariablesKeys } from '@api/enterprise/globalVariable/constants';
import type { CustomerAcknowledgmentConfig } from '../types';
import {
  parseAcknowledgmentConfig,
  stringifyAcknowledgmentConfig
} from '../utils';

const VARIABLE_NAME = 'HIDE_CUSTOMER_ACKNOWLEDGMENT';

export const useAcknowledgmentConfigData = () => {
  const queryClient = useQueryClient();

  const {
    data: globalVariable,
    isLoading,
    error,
    refetch
  } = useGlobalVariable(VARIABLE_NAME);

  const updateMutation = useUpdateGlobalVariable();

  const [customStatement, setCustomStatement] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const originalConfig = useMemo((): CustomerAcknowledgmentConfig => {
    return parseAcknowledgmentConfig(globalVariable?.blobText);
  }, [globalVariable?.blobText]);

  useEffect(() => {
    setCustomStatement(originalConfig.statement || '');
    setHasUnsavedChanges(false);
  }, [originalConfig]);

  const updateStatement = useCallback((statement: string) => {
    setCustomStatement(statement);
    setHasUnsavedChanges(true);
  }, []);

  const clearCustomStatement = useCallback(() => {
    setCustomStatement('');
    setHasUnsavedChanges(true);
  }, []);

  const saveChanges = useCallback(async () => {
    try {
      const config: CustomerAcknowledgmentConfig = {
        statement: customStatement.trim() || null,
        version: 1
      };
      const jsonString = stringifyAcknowledgmentConfig(config);

      await updateMutation.mutateAsync({
        name: VARIABLE_NAME,
        command: {
          blobText: jsonString,
          text: globalVariable?.text || null,
          int: globalVariable?.int || null,
          date: globalVariable?.date || null,
          numeric: globalVariable?.numeric || null
        }
      });

      await queryClient.invalidateQueries({
        queryKey: globalVariablesKeys.byName(VARIABLE_NAME)
      });

      await refetch();
      setHasUnsavedChanges(false);

      return { success: true };
    } catch (err) {
      setHasUnsavedChanges(true);
      throw err;
    }
  }, [customStatement, globalVariable, updateMutation, queryClient, refetch]);

  const discardChanges = useCallback(() => {
    setCustomStatement(originalConfig.statement || '');
    setHasUnsavedChanges(false);
  }, [originalConfig]);

  return {
    customStatement,
    originalConfig,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving: updateMutation.isPending,
    saveError: updateMutation.error,
    updateStatement,
    clearCustomStatement,
    saveChanges,
    discardChanges,
    refresh: refetch
  };
};

export default useAcknowledgmentConfigData;
